import { useEffect, useMemo, useState, useCallback } from "react";
import { Building2, Plus, Copy, Search, FileSpreadsheet, FileText, Printer, LogOut, Trash2, Calendar, Download, HandCoins, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { aggregate, fmtRange, DAYS } from "../lib/calc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SummaryWidgets from "../components/SummaryWidgets";
import PayrollTable from "../components/PayrollTable";
import WorkerDialog from "../components/WorkerDialog";
import ProjectsDialog from "../components/ProjectsDialog";
import WeekDialog from "../components/WeekDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import InstallmentLoansDialog from "../components/InstallmentLoansDialog";
import WorkerQuickEditDialog from "../components/WorkerQuickEditDialog";
import ObrasReportDialog from "../components/ObrasReportDialog";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [weeks, setWeeks] = useState([]);
  const [weekId, setWeekId] = useState("");
  const [projects, setProjects] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [filterProject, setFilterProject] = useState("all");
  const [search, setSearch] = useState("");
  const [weekDialog, setWeekDialog] = useState({ open: false, dup: null });
  const [confirmDelWeek, setConfirmDelWeek] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfName, setPdfName] = useState("nomina.pdf");
  const [loansEntry, setLoansEntry] = useState(null);
  const [quickEdit, setQuickEdit] = useState(null);
  const [obrasReport, setObrasReport] = useState(false);

  const loadWeeks = useCallback(async () => {
    const { data } = await api.get("/weeks");
    setWeeks(data);
    if (data.length && !data.find((w) => w.id === weekId)) setWeekId(data[0].id);
    if (!data.length) { setWeekId(""); setEntries([]); }
  }, [weekId]);

  const loadMeta = useCallback(async () => {
    const [p, w] = await Promise.all([api.get("/projects"), api.get("/workers")]);
    setProjects(p.data); setWorkers(w.data);
  }, []);

  useEffect(() => { loadMeta().catch(err); loadWeeks().catch(err); }, []); // eslint-disable-line

  const loadEntries = useCallback(async (id) => {
    if (!id) return;
    const { data } = await api.get(`/weeks/${id}/entries`);
    setEntries(data);
  }, []);

  useEffect(() => { loadEntries(weekId).catch(err); }, [weekId, loadEntries]);

  const currentWeek = weeks.find((w) => w.id === weekId);

  // ---- weeks ----
  const createWeek = async (start, end, dup) => {
    try {
      const { data } = await api.post("/weeks", { start_date: start, end_date: end, duplicate_from: dup });
      await loadWeeks();
      setWeekId(data.id);
      toast.success(dup ? "Semana duplicada" : "Semana creada");
    } catch (e) { err(e); }
  };
  const deleteWeek = async () => {
    if (!weekId) return;
    try { await api.delete(`/weeks/${weekId}`); setWeekId(""); await loadWeeks(); toast.success("Semana eliminada"); }
    catch (e) { err(e); }
    finally { setConfirmDelWeek(false); }
  };

  // ---- export ----
  const doExport = async (kind, extra = null) => {
    if (!weekId) return toast.error("Selecciona una semana");
    if (!(extra ? entries : filtered).length) return toast.error("No hay registros para exportar");
    const isPdf = kind !== "excel";
    try {
      const params = new URLSearchParams(extra || { project_id: filterProject, q: search });
      const res = await api.get(`/weeks/${weekId}/export/${kind}?${params.toString()}`, { responseType: "blob" });
      const type = isPdf
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([res.data], { type });
      const url = URL.createObjectURL(blob);
      const prefix = { loans: "prestamos", obras: "gasto_obras" }[kind] || "nomina";
      const fname = `${prefix}_vicon_${currentWeek?.start_date || "semana"}.${isPdf ? "pdf" : "xlsx"}`;
      // Descarga automática del archivo
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      if (isPdf) {
        setPdfName(fname);
        setPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; }); // vista previa en la app
        toast.success({ loans: "PDF de préstamos descargado", obras: "Reporte de gasto por obra descargado" }[kind] || "PDF descargado");
      } else {
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        toast.success("Excel descargado");
      }
    } catch (e) {
      toast.error("No se pudo exportar el archivo");
    }
  };
  const doExportExcel = () => doExport("excel");
  const doExportPDF = () => doExport("pdf");
  const doExportLoans = () => doExport("loans");
  const doExportObras = async (ids) => { setObrasReport(false); await doExport("obras", { project_ids: ids.join(",") }); };
  const closePdf = () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); setPdfUrl(null); };

  const downloadSobre = async (eid, name) => {
    try {
      const res = await api.get(`/entries/${eid}/sobre`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = URL.createObjectURL(blob);
      const safe = (name || "trabajador").replace(/\s+/g, "_");
      const a = document.createElement("a");
      a.href = url; a.download = `sobre_${safe}_${currentWeek?.start_date || "semana"}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`Sobre de ${name} descargado`);
    } catch (e) {
      toast.error("No se pudo generar el sobre");
    }
  };

  const downloadObraSobres = async (projectId, obraName) => {
    try {
      const params = new URLSearchParams({ project_id: projectId || "all", q: search });
      const res = await api.get(`/weeks/${weekId}/sobres?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = URL.createObjectURL(blob);
      const safe = (obraName || "obra").replace(/\s+/g, "_");
      const a = document.createElement("a");
      a.href = url; a.download = `sobres_${safe}_${currentWeek?.start_date || "semana"}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`Sobres de ${obraName} descargados`);
    } catch (e) {
      toast.error("No se pudieron generar los sobres");
    }
  };

  // ---- entries ----
  const saveEntry = async (entry) => {
    try {
      const loans = {};
      DAYS.forEach(({ key }) => { loans[key] = Number(entry.loans?.[key]) || 0; });
      await api.put(`/entries/${entry.id}`, { attendance: entry.attendance, loans });
    }
    catch (e) { err(e); }
  };
  const onAttendance = (id, dayKey) => {
    setEntries((prev) => prev.map((e) => {
      if (e.id !== id) return e;
      const cur = e.attendance?.[dayKey];
      const next = cur == null ? 1 : cur === 1 ? 0.5 : cur === 0.5 ? 0 : null;
      const updated = { ...e, attendance: { ...e.attendance, [dayKey]: next } };
      saveEntry(updated);
      return updated;
    }));
  };
  const onLoanChange = (id, dayKey, value) => {
    setEntries((prev) => prev.map((e) => e.id === id ? { ...e, loans: { ...e.loans, [dayKey]: value } } : e));
  };
  const onLoanCommit = (id) => {
    setEntries((prev) => {
      const e = prev.find((x) => x.id === id);
      if (e) saveEntry(e); // guarda el valor más reciente (incluye préstamos borrados = 0)
      return prev;
    });
  };
  const toggleMark = (id) => {
    setEntries((prev) => prev.map((e) => {
      if (e.id !== id) return e;
      const updated = { ...e, marcado: !e.marcado };
      api.put(`/entries/${id}`, { marcado: updated.marcado }).catch(err);
      return updated;
    }));
  };

  // ---- workers ----
  const createWorker = async (payload) => {
    try {
      await api.post("/workers", payload);
      // Agregar el trabajador SOLO a la semana actual (no afecta semanas anteriores)
      if (weekId) await api.post(`/weeks/${weekId}/sync`);
      await loadMeta();
      await loadEntries(weekId);
      toast.success("Trabajador agregado");
    } catch (e) { err(e); }
  };
  const updateWorker = async (id, payload) => {
    try {
      await api.put(`/workers/${id}`, payload, { params: weekId ? { week_id: weekId } : {} });
      await loadMeta();
      await loadEntries(weekId);
      toast.success("Trabajador actualizado");
    } catch (e) { err(e); }
  };
  const deleteWorker = async (id) => {
    try {
      // Eliminar solo de la semana actual; las semanas anteriores conservan su historial
      await api.delete(`/workers/${id}`, { params: weekId ? { week_id: weekId } : {} });
      await loadMeta();
      await loadEntries(weekId);
      toast.success("Trabajador eliminado");
    } catch (e) { err(e); }
  };

  // ---- projects ----
  const createProject = async (name) => { try { await api.post("/projects", { name }); await loadMeta(); toast.success("Obra creada"); } catch (e) { err(e); } };
  const updateProject = async (id, name) => { try { await api.put(`/projects/${id}`, { name }); await loadMeta(); toast.success("Obra actualizada"); } catch (e) { err(e); } };
  const deleteProject = async (id) => {
    try { await api.delete(`/projects/${id}`); await loadMeta(); toast.success("Obra eliminada"); } catch (e) { err(e); }
  };

  const filtered = useMemo(() => entries.filter((e) => {
    const okP = filterProject === "all" || (filterProject === "none" ? !e.project_id : e.project_id === filterProject);
    const okS = !search || e.worker_name.toLowerCase().includes(search.toLowerCase());
    return okP && okS;
  }), [entries, filterProject, search]);

  const totals = aggregate(filtered);

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white px-6 py-3 flex justify-between items-center no-print">
        <div className="flex items-center gap-2.5">
          <div className="bg-blue-600 p-1.5 rounded-md"><Building2 size={20} /></div>
          <div>
            <h1 className="font-heading font-bold text-lg tracking-tight leading-none">VICON Construcciones</h1>
            <p className="text-[11px] text-slate-400">Nómina semanal · {user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={doExportExcel} disabled={!filtered.length} data-testid="export-excel" className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white"><FileSpreadsheet size={15} className="mr-1.5" />Excel</Button>
          <Button variant="outline" size="sm" onClick={doExportPDF} disabled={!filtered.length} data-testid="export-pdf" className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white"><FileText size={15} className="mr-1.5" />PDF</Button>
          <Button variant="outline" size="sm" onClick={doExportLoans} disabled={!filtered.length} data-testid="export-loans" className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white"><HandCoins size={15} className="mr-1.5" />Préstamos</Button>
          <Button variant="outline" size="sm" onClick={() => setObrasReport(true)} disabled={!entries.length} data-testid="export-obras" className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white"><BarChart3 size={15} className="mr-1.5" />Gasto por obra</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-btn" className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white"><Printer size={15} className="mr-1.5" />Imprimir</Button>
          <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn" className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white"><LogOut size={15} className="mr-1.5" />Salir</Button>
        </div>
      </header>

      {/* Control bar */}
      <div className="px-6 py-3 bg-white border-b border-slate-200 flex flex-wrap items-center gap-2 no-print">
        <Calendar size={16} className="text-slate-400" />
        <Select value={weekId} onValueChange={setWeekId}>
          <SelectTrigger className="w-[230px] h-9" data-testid="week-select"><SelectValue placeholder="Selecciona semana" /></SelectTrigger>
          <SelectContent>
            {weeks.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Sin semanas</div>}
            {weeks.map((w) => <SelectItem key={w.id} value={w.id}>{fmtRange(w.start_date, w.end_date)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => setWeekDialog({ open: true, dup: null })} data-testid="btn-new-week" className="bg-slate-900 hover:bg-slate-800 h-9"><Plus size={15} className="mr-1" />Nueva semana</Button>
        <Button size="sm" variant="outline" disabled={!weekId} onClick={() => setWeekDialog({ open: true, dup: weekId })} data-testid="btn-duplicate-week" className="border-slate-300 h-9"><Copy size={15} className="mr-1" />Duplicar anterior</Button>
        {weekId && <Button size="sm" variant="ghost" onClick={() => setConfirmDelWeek(true)} data-testid="btn-delete-week" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9"><Trash2 size={15} /></Button>}

        <div className="h-6 w-px bg-slate-200 mx-1" />

        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-[170px] h-9" data-testid="filter-project"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las obras</SelectItem>
            <SelectItem value="none">Sin obra</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar trabajador..." data-testid="search-worker" className="pl-8 h-9 w-[200px]" />
        </div>

        <div className="ml-auto flex gap-2">
          <WorkerDialog workers={workers} projects={projects} onCreate={createWorker} onUpdate={updateWorker} onDelete={deleteWorker} />
          <ProjectsDialog projects={projects} onCreate={createProject} onUpdate={updateProject} onDelete={deleteProject} />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col gap-3 p-6 overflow-hidden">
        <SummaryWidgets totals={totals} workerCount={filtered.length} />
        {!weekId ? (
          <div className="flex-1 flex items-center justify-center border border-dashed border-slate-300 rounded-sm text-center">
            <div>
              <Calendar size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No hay ninguna semana seleccionada.</p>
              <p className="text-sm text-slate-400">Crea una nueva semana para comenzar la nómina.</p>
            </div>
          </div>
        ) : (
          <PayrollTable entries={filtered} onAttendance={onAttendance} onLoanChange={onLoanChange} onLoanCommit={onLoanCommit} onSobre={downloadSobre} onToggleMark={toggleMark} onOpenLoans={setLoansEntry} onWorkerClick={setQuickEdit} onDownloadGroupSobres={downloadObraSobres} />
        )}
      </div>

      <WeekDialog open={weekDialog.open} onOpenChange={(o) => setWeekDialog({ ...weekDialog, open: o })} onCreate={createWeek} duplicateFrom={weekDialog.dup} />
      <ConfirmDialog open={confirmDelWeek} onOpenChange={setConfirmDelWeek} testid="confirm-delete-week"
        title="Eliminar semana" description="Se eliminará esta semana y todos sus registros de asistencia y préstamos. Esta acción no se puede deshacer."
        onConfirm={deleteWeek} />

      <ObrasReportDialog open={obrasReport} onOpenChange={setObrasReport} entries={entries} onDownload={doExportObras} />

      <InstallmentLoansDialog entry={loansEntry} weekId={weekId} onClose={() => setLoansEntry(null)}
        onChanged={() => loadEntries(weekId)} />

      <WorkerQuickEditDialog entry={quickEdit} projects={projects} onClose={() => setQuickEdit(null)}
        onSave={async (id, p) => { await updateWorker(id, p); setQuickEdit(null); }}
        onDelete={async (id) => { await deleteWorker(id); setQuickEdit(null); }} />

      <Dialog open={!!pdfUrl} onOpenChange={(o) => !o && closePdf()}>
        <DialogContent className="max-w-6xl w-[95vw] h-[92vh] flex flex-col p-0 gap-0 rounded-sm [&>button]:text-white [&>button]:opacity-100 [&>button]:top-3" data-testid="pdf-preview-dialog">
          <DialogTitle className="sr-only">Vista previa de nómina en PDF</DialogTitle>
          <DialogDescription className="sr-only">Documento PDF de la nómina semanal. Usa el botón Descargar para guardarlo.</DialogDescription>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-900 text-white">
            <span className="font-heading font-semibold text-sm flex items-center gap-2"><FileText size={16} /> Vista previa — {pdfName}</span>
            <div className="flex items-center gap-2 mr-6">
              <a href={pdfUrl || "#"} download={pdfName} data-testid="pdf-download"
                className="inline-flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-sm transition-colors">
                <Download size={14} /> Descargar
              </a>
            </div>
          </div>
          {pdfUrl && <iframe src={pdfUrl} title="Vista previa PDF" className="flex-1 w-full bg-slate-100" data-testid="pdf-frame" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
